import React, { useState } from 'react';
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  FileText,
  RefreshCw,
  Clock,
  Shield,
  Download,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Zap,
  Check,
  Tag,
  Receipt,
  RotateCcw,
  Sliders,
  DollarSign
} from 'lucide-react';
import { AppView } from '../../types';
import {
  BILLING_PRODUCTS,
  MOCK_SUBSCRIPTIONS,
  MOCK_INVOICES,
  MOCK_PAYMENT_TRANSACTIONS,
  MOCK_COUPONS
} from '../../data/billingMockData';
import { BillingProduct, CanonicalSubscription, BillingInvoice } from '../../services/billing/billingTypes';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const CustomerBillingPortal: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<'current-plan' | 'upgrade' | 'invoices' | 'payment-methods' | 'usage'>('current-plan');
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [checkoutModalProduct, setCheckoutModalProduct] = useState<BillingProduct | null>(null);
  const [checkoutSuccessMessage, setCheckoutSuccessMessage] = useState<string | null>(null);

  // Active user's subscription (demo state)
  const currentSub: CanonicalSubscription = MOCK_SUBSCRIPTIONS[0]; // Developer Growth

  const handleApplyCoupon = (e: React.FormEvent) => {
    e.preventDefault();
    const found = MOCK_COUPONS.find(c => c.code.toUpperCase() === couponInput.trim().toUpperCase());
    if (found) {
      setAppliedCoupon(found.code);
      setCouponInput('');
    } else {
      alert('Invalid coupon code. Try BUILDER50 or YAMOOANNUAL.');
    }
  };

  const handleInitiateCheckout = (prod: BillingProduct) => {
    setCheckoutModalProduct(prod);
  };

  const handleConfirmMockCheckout = (provider: 'STRIPE' | 'MONEROO' | 'CHARIOW') => {
    setCheckoutModalProduct(null);
    setCheckoutSuccessMessage(`Checkout completed successfully via ${provider}! Your plan and entitlements have been updated.`);
    setTimeout(() => setCheckoutSuccessMessage(null), 6000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#1E40AF] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Receipt className="w-4 h-4 text-[#38BDF8]" />
            <span>Customer Account</span>
            <span>/</span>
            <span className="text-white font-bold">Billing & Subscription Management</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                Customer Billing Portal
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Manage your active YamooPay plan, payment methods, download VAT/tax invoices, and monitor your monthly compute unit usage.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigateToView('billing-command-center')}
                className="px-4 py-2.5 bg-blue-900/60 hover:bg-blue-900 border border-blue-400/40 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Sliders className="w-4 h-4 text-[#38BDF8]" />
                <span>Admin Billing Command Center</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Navigation Sub-Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'current-plan', label: 'Active Plan & Entitlements', icon: Shield },
              { id: 'upgrade', label: 'Explore Plans & Upgrade', icon: Sparkles },
              { id: 'usage', label: 'API Metering & Compute Units', icon: Zap },
              { id: 'invoices', label: `Invoices & Receipts (${MOCK_INVOICES.length})`, icon: FileText },
              { id: 'payment-methods', label: 'Payment Methods & Cards', icon: CreditCard }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {checkoutSuccessMessage && (
            <div className="p-4 mb-6 bg-emerald-50 border border-emerald-200 text-emerald-950 rounded-xl text-sm font-bold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>{checkoutSuccessMessage}</span>
            </div>
          )}

          {/* TAB 1: CURRENT PLAN */}
          {activeTab === 'current-plan' && (
            <div className="space-y-8">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6 relative overflow-hidden">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-[#1E40AF] text-xs font-bold rounded-full">
                        ACTIVE SUBSCRIPTION
                      </span>
                      <span className="text-xs font-mono text-[#64748B]">{currentSub.id}</span>
                    </div>
                    <h2 className="text-2xl font-black text-[#0F172A]">{currentSub.productName}</h2>
                    <p className="text-sm text-[#64748B] mt-1">
                      Billed to <strong>{currentSub.customerEmail}</strong> via <strong>{currentSub.provider}</strong>.
                    </p>
                  </div>

                  <div className="text-left md:text-right">
                    <div className="text-3xl font-black text-[#1E40AF]">{currentSub.amountFormatted}</div>
                    <div className="text-xs text-[#64748B] mt-1">
                      Next renewal: <strong>August 31, 2026</strong>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex flex-wrap gap-4">
                  <button
                    onClick={() => setActiveTab('upgrade')}
                    className="px-5 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold rounded-xl text-sm transition-colors cursor-pointer"
                  >
                    Change Plan / Upgrade
                  </button>
                  <button
                    onClick={() => alert('Subscription management action triggered. Provider billing session would launch.')}
                    className="px-5 py-2.5 bg-white border border-[#CBD5E1] hover:bg-[#F1F5F9] text-[#0F172A] font-bold rounded-xl text-sm transition-colors cursor-pointer"
                  >
                    Cancel Subscription
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">Included Plan Entitlements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: '10,000,000 Compute Units / Month', desc: 'Included API calls across FX, salary tax, compound interest & retirement calculation engines.' },
                    { title: '2,500 Requests / Minute Rate Limit', desc: 'Burst and continuous throughput protection with enterprise high availability.' },
                    { title: 'White-Label Widget Permissions', desc: 'Embed branded calculators with custom CSS color tokens and zero YamooPay watermarks.' },
                    { title: 'Priority Developer SLA & Webhooks', desc: 'Signed HMAC webhooks with automated 72-hour backoff retry delivery.' }
                  ].map((ent, i) => (
                    <div key={i} className="p-4 bg-white border border-[#E2E8F0] rounded-xl flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-sm text-[#0F172A]">{ent.title}</h4>
                        <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{ent.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: UPGRADE / EXPLORE PLANS */}
          {activeTab === 'upgrade' && (
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div>
                  <h3 className="text-2xl font-black text-[#0F172A]">Choose the Right Tier</h3>
                  <p className="text-sm text-[#64748B]">All plans include our high-assurance calculation algorithms.</p>
                </div>

                {/* Interval Toggle */}
                <div className="flex items-center p-1 bg-[#F1F5F9] rounded-xl border border-[#E2E8F0]">
                  <button
                    onClick={() => setSelectedBillingInterval('MONTHLY')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      selectedBillingInterval === 'MONTHLY' ? 'bg-white shadow-xs text-[#0F172A]' : 'text-[#64748B]'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setSelectedBillingInterval('ANNUAL')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                      selectedBillingInterval === 'ANNUAL' ? 'bg-[#1E40AF] shadow-xs text-white' : 'text-[#64748B]'
                    }`}
                  >
                    <span>Annual</span>
                    <span className="px-1.5 py-0.2 bg-emerald-400 text-emerald-950 rounded text-[9px] font-black">SAVE 20%</span>
                  </button>
                </div>
              </div>

              {/* Coupon Bar */}
              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#1E40AF]" />
                  <span className="text-xs font-bold text-[#0F172A]">Have a promo or partner voucher code?</span>
                </div>

                <form onSubmit={handleApplyCoupon} className="flex gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="e.g. BUILDER50"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    className="px-3 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-mono uppercase focus:outline-hidden focus:ring-2 focus:ring-[#1E40AF]"
                  />
                  <button
                    type="submit"
                    className="px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    Apply
                  </button>
                </form>

                {appliedCoupon && (
                  <span className="text-xs font-bold text-[#059669] flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Code {appliedCoupon} applied!
                  </span>
                )}
              </div>

              {/* Plan Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {BILLING_PRODUCTS.map((p) => {
                  const isCurrent = p.id === currentSub.productId;
                  const price = selectedBillingInterval === 'MONTHLY' ? p.monthlyBasePriceUsd : (p.annualBasePriceUsd / 12);
                  const priceDisplay = selectedBillingInterval === 'MONTHLY' ? `$${p.monthlyBasePriceUsd.toFixed(2)}/mo` : `$${p.annualBasePriceUsd.toFixed(2)}/yr`;

                  return (
                    <div
                      key={p.id}
                      className={`rounded-2xl p-6 border flex flex-col justify-between transition-all ${
                        isCurrent
                          ? 'border-[#1E40AF] bg-blue-50/40 ring-2 ring-[#1E40AF]/20'
                          : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
                      }`}
                    >
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-bold text-[#1E40AF] font-mono">{p.productType}</span>
                          {isCurrent && (
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded">
                              CURRENT
                            </span>
                          )}
                        </div>

                        <h4 className="text-lg font-black text-[#0F172A] mb-2">{p.name}</h4>
                        <p className="text-xs text-[#64748B] leading-relaxed mb-4">{p.description}</p>

                        <div className="mb-6">
                          <span className="text-3xl font-black text-[#0F172A]">{priceDisplay}</span>
                          {selectedBillingInterval === 'ANNUAL' && (
                            <span className="block text-xs text-[#059669] font-bold mt-0.5">Billed annually (${p.annualBasePriceUsd}/yr)</span>
                          )}
                        </div>

                        <div className="space-y-2 mb-6">
                          <span className="text-xs font-bold text-[#475569]">Features included:</span>
                          {p.entitlements.map((e) => (
                            <div key={e} className="flex items-center gap-2 text-xs text-[#475569]">
                              <Check className="w-3.5 h-3.5 text-[#059669] shrink-0" />
                              <span className="font-mono text-[11px]">{e}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        {isCurrent ? (
                          <button
                            disabled
                            className="w-full py-2.5 bg-blue-100 text-[#1E40AF] font-bold rounded-xl text-xs cursor-default"
                          >
                            Current Plan
                          </button>
                        ) : (
                          <button
                            onClick={() => handleInitiateCheckout(p)}
                            className="w-full py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold rounded-xl text-xs transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                          >
                            <span>Select {p.name.split(' ')[0]}</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 3: USAGE METERING */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">API Compute Units & Usage Telemetry</h3>
                <p className="text-sm text-[#64748B]">Real-time consumption for billing period August 1 - August 31, 2026.</p>
              </div>

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-bold text-[#0F172A]">Included Compute Units Consumed</span>
                  <span className="text-sm font-mono font-bold text-[#1E40AF]">3,420,000 / 10,000,000 (34.2%)</span>
                </div>
                <div className="w-full bg-[#E2E8F0] h-3 rounded-full overflow-hidden">
                  <div className="bg-[#1E40AF] h-full rounded-full transition-all duration-500" style={{ width: '34.2%' }} />
                </div>
                <div className="flex justify-between text-xs text-[#64748B] mt-2">
                  <span>Overage Rate: $0.00005 per unit above 10M</span>
                  <span>Estimated Overage: $0.00</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: INVOICES */}
          {activeTab === 'invoices' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Billing Invoices & Receipts</h3>
                <p className="text-sm text-[#64748B]">Download PDF copies of your historical invoices.</p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Invoice Number</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Date</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Amount Paid</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_INVOICES.map((inv) => (
                      <tr key={inv.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-mono font-bold text-[#0F172A] text-xs">{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 font-mono font-bold text-[#0F172A] text-xs">${inv.amountPaid.toFixed(2)} {inv.currency}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => alert(`Downloading PDF for invoice ${inv.invoiceNumber}`)}
                            className="text-xs font-bold text-[#1E40AF] hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: PAYMENT METHODS */}
          {activeTab === 'payment-methods' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Saved Payment Methods</h3>
                <p className="text-sm text-[#64748B]">All transactions are encrypted and tokenized via certified PCI-DSS Level 1 vaults.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 border border-[#E2E8F0] rounded-2xl bg-white flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-8 bg-[#0F172A] rounded-md flex items-center justify-center text-white font-bold text-xs tracking-wider">
                      VISA
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0F172A]">Visa ending in 4242</div>
                      <div className="text-xs text-[#64748B]">Expires 12/2028 • Default Payment Method</div>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                    DEFAULT
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CHECKOUT MODAL */}
      {checkoutModalProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-black text-[#0F172A]">Complete Checkout</h3>
                <p className="text-xs text-[#64748B] mt-0.5">Secure payment provider gateway selection</p>
              </div>
              <button
                onClick={() => setCheckoutModalProduct(null)}
                className="text-[#64748B] hover:text-[#0F172A] text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-2">
              <div className="flex justify-between font-bold text-sm text-[#0F172A]">
                <span>{checkoutModalProduct.name}</span>
                <span>${selectedBillingInterval === 'MONTHLY' ? checkoutModalProduct.monthlyBasePriceUsd.toFixed(2) : checkoutModalProduct.annualBasePriceUsd.toFixed(2)}</span>
              </div>
              <p className="text-xs text-[#64748B]">{checkoutModalProduct.description}</p>
            </div>

            <div className="space-y-3">
              <span className="text-xs font-bold text-[#475569]">Select Payment Gateway:</span>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => handleConfirmMockCheckout('STRIPE')}
                  className="p-3 border border-[#CBD5E1] hover:border-[#1E40AF] rounded-xl text-center hover:bg-blue-50/50 transition-all cursor-pointer"
                >
                  <strong className="block text-xs text-[#0F172A]">Stripe</strong>
                  <span className="text-[10px] text-[#64748B]">Card / Apple Pay</span>
                </button>
                <button
                  onClick={() => handleConfirmMockCheckout('MONEROO')}
                  className="p-3 border border-[#CBD5E1] hover:border-[#1E40AF] rounded-xl text-center hover:bg-blue-50/50 transition-all cursor-pointer"
                >
                  <strong className="block text-xs text-[#0F172A]">Moneroo</strong>
                  <span className="text-[10px] text-[#64748B]">Mobile Money / Cards</span>
                </button>
                <button
                  onClick={() => handleConfirmMockCheckout('CHARIOW')}
                  className="p-3 border border-[#CBD5E1] hover:border-[#1E40AF] rounded-xl text-center hover:bg-blue-50/50 transition-all cursor-pointer"
                >
                  <strong className="block text-xs text-[#0F172A]">Chariow</strong>
                  <span className="text-[10px] text-[#64748B]">Pulse Checkout</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-[#E2E8F0]">
              <button
                onClick={() => setCheckoutModalProduct(null)}
                className="px-4 py-2 bg-[#F1F5F9] text-[#475569] text-xs font-bold rounded-xl hover:bg-[#E2E8F0] cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
